import { zodResolver } from "@hookform/resolvers/zod";
import { Anchor, Button, Divider, Group, LoadingOverlay, Radio, Stack, Text } from "@mantine/core";
import { WarningIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Control, Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { MdPlaylistAddCheck } from "react-icons/md";
import { toast } from "react-toastify";
import { z } from "zod";

import { RadioOrder } from "@/components/@custom/RadioOrder";
import { useLoader } from "@/contexts/loader";
import { useSession } from "@/hooks/@custom/useSession";
import { QueueHeader } from "@/pages/App/Driver/components/QueueHeader";
import { SaveQueue, useGetSettingsByCompanyQuery, useLazyGetTruckByDriverQuery, useSaveQueueMutation } from "@/store/apis/nextcargo";
import {
  GetSchedulingByDriverLicensePlate,
  useLazyGetSchedulingByDriverLicensePlateQuery,
  useLazyGetSchedulingQuery,
  useLazyGetSchedulingsByDriverQuery,
  useSaveSchedulingDeleteMutation
} from "@/store/apis/nextcargo/scheduling";
import { cn } from "@/utils/cn";

interface Truck {
  id: number;
  vehicleModel: { name: string };
  licensePlate: string;
  color: string;
}

export interface Customer {
  code: number;
  name: string;
  city: string;
}

export interface ShippingCompany {
  name: string;
}

export interface Order {
  id: number;
  status: string;
  code: string;
  customer: Customer;
  shippingCompany: ShippingCompany[];
  orderItem: OrderItem[];
}

interface Product {
  code: number;
  name: string;
}

interface OrderItem {
  id: number;
  quantity: number;
  product: Product;
}

export type Scheduling = NextApp.SchedulingByDriverAndPlate & {
  truck: {
    id?: number;
  };
};
export type QueueBody = { truckId: number; schedulingId: number };

export type SchedulingSchema = z.infer<typeof schema>;
const schema = z.object({
  truck: z.object({
    plate: z.string({ required_error: "Campo obrigatório!" }).nonempty("Selecione um caminhão!")
  }),
  order: z
    .object({
      id: z.string()
    })
    .nullish()
});

export const Route = createFileRoute("/app/agendamento/")({
  component: DriverScheduling
});

type Error = {
  status: number;
  message1: string;
  message2: string;
  message3: string;
};

function DriverScheduling() {
  const { driver } = useSession();
  const navigate = useNavigate();
  const { isLoading, setLoading } = useLoader();

  const [saveQueue] = useSaveQueueMutation();
  const [saveDeleteScheduling] = useSaveSchedulingDeleteMutation();
  const [getSchedulingByDriverLicensePlate, { data: schedulingByDriverLicensePlate }] = useLazyGetSchedulingByDriverLicensePlateQuery();
  const [getSchedulingsByDriver] = useLazyGetSchedulingsByDriverQuery();
  const [getSchedulingById] = useLazyGetSchedulingQuery();
  const [getTruckByDriver] = useLazyGetTruckByDriverQuery();

  const [error, setError] = useState<Error>({
    status: 0,
    message1: "",
    message2: "",
    message3: ""
  });

  const [queueSaved, setQueueSaved] = useState(false);
  const [, setSubmitStatus] = useState<{ success: boolean; error: boolean }>({ success: false, error: false });

  //! Trucks
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const truckTotal = trucks.length;
  const schedulingTotal = schedulingByDriverLicensePlate?.length || 0;
  const companyId = driver?.company.id;
  const driverId = driver?.id;

  const methods = useForm<SchedulingSchema>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: async () => {
      const defaultData = {
        truck: {
          plate: ""
        },
        order: {
          id: "0"
        }
      };

      setLoading(true);
      try {
        const promises = [getTruckByDriver({ id: driverId! }), getSchedulingsByDriver({ id: driverId! })];
        const [truckResponse, schedulingResponse] = await Promise.all(promises);
        const schedulingTrucks = (schedulingResponse.data as unknown as NextApp.SchedulingByDriver[]).map((item) => item.truck);

        if (schedulingTrucks.length === 0) {
          setIfAnon(true);
          setAnonPage(1);

          const newArr = (truckResponse.data as TruckData[])?.map((item) => ({
            id: item.id,
            vehicleModel: item.vehicleModel,
            licensePlate: item.licensePlate,
            color: item.color
          }));
          setTrucks(newArr as Truck[]);

          return {
            truck: {
              plate: newArr[0]?.licensePlate
            },
            order: {
              id: "0"
            }
          };
        }

        const arr: Truck[] = [];
        schedulingTrucks.forEach((item) => {
          const truckDataItem = (truckResponse.data as TruckData[]).find((truck) => truck.licensePlate === item.licensePlate);
          const alreadyExists = arr.some((x) => x.licensePlate === item.licensePlate);

          if (!alreadyExists && truckDataItem) {
            arr.push({
              id: truckDataItem.id,
              vehicleModel: truckDataItem.vehicleModel,
              licensePlate: truckDataItem.licensePlate,
              color: truckDataItem.color
            });
          }
        });
        setTrucks(arr);

        return {
          truck: {
            plate: schedulingTrucks[0]?.licensePlate
          },
          order: {
            id: defaultData.order.id
          }
        };
      } catch {
        return defaultData;
      } finally {
        setLoading(false);
      }
    }
  });
  const orderId = methods.watch("order.id");

  async function createQueue({ schedulingId, truckId }: Omit<QueueBody, "truckId"> & { truckId?: number }) {
    if (!truckId) {
      setSubmitStatus((prev) => ({ ...prev, error: true }));
      return toast.error("Caminhão não encontrado!");
    }

    const body: SaveQueue["args"] = {
      driver: { id: +driverId! },
      company: { id: +companyId! },
      truck: { id: truckId },
      scheduling: schedulingId ? { id: schedulingId } : null
    };

    const { error } = await saveQueue(body);

    if (!error) {
      setQueueSaved(true);
      setSubmitStatus((prev) => ({ ...prev, success: true }));
      navigate({ to: "/app/agendamento/status", replace: true });
      return;
    }

    setError(error as Error);
    setSubmitStatus((prev) => ({ ...prev, error: true }));
    navigate({ to: "/", replace: true });
  }

  const onSubmit: SubmitHandler<SchedulingSchema> = async (data) => {
    const { truck, order } = data;

    const truckPlate = truck.plate;
    const truckId = trucks.find((item) => item.licensePlate === truckPlate)?.id;
    const schedulingId = Number(order?.id);

    await createQueue({ schedulingId, truckId });

    methods.reset();
  };

  const handleFrelancerClick = () => {
    methods.setValue("order", null);
  };

  const onInvalid: SubmitErrorHandler<SchedulingSchema> = (errors) => {
    console.log({ errors });
  };

  const denyOrder = async (id: number) => {
    const { error } = await saveDeleteScheduling({ id });

    if (!error && schedulingByDriverLicensePlate?.[0]) {
      methods.reset({
        order: { id: schedulingByDriverLicensePlate[0].id ? String(schedulingByDriverLicensePlate[0].id) : "0" },
        truck: { plate: schedulingByDriverLicensePlate[0].truck?.licensePlate || "" }
      });
    }
  };

  const acceptOrder = (body: QueueBody) => {
    createQueue(body);
  };

  useEffect(() => {
    if (schedulingTotal === 0) {
      methods.unregister("order");
    }
  }, [schedulingTotal]);

  /* ############################################################ */

  const [anonPage, setAnonPage] = useState(0);
  const [truckId, setTruckId] = useState(0);
  const [ifAnon, setIfAnon] = useState(false);

  async function handleNext() {
    const selectedPlate = methods.getValues("truck.plate");
    setLoading(true);
    const schedulingByDriverLicensePlateParams: GetSchedulingByDriverLicensePlate["args"] = {
      driverId: driverId!,
      licensePlate: selectedPlate,
      companyId: companyId,
      $findTruck: true
    };

    try {
      const { data } = await getSchedulingByDriverLicensePlate(schedulingByDriverLicensePlateParams);
      const schedulingId = data?.[0]?.id;

      if (schedulingId) {
        const { data: schedulingData } = await getSchedulingById({ id: schedulingId });
        const truckId = schedulingData!.truck.id;
        const plate = methods.getValues("truck.plate");
        setTruckId(truckId);
        methods.reset({ order: { id: String(schedulingId) }, truck: { plate } });
      }
    } catch (error) {
      console.error("Error fetching scheduling data:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleConfirmAnon() {
    if (truckTotal > 0) {
      setAnonPage(2);
    } else if (truckTotal === 0) {
      setAnonPage(1);
      if (!isLoading) {
        setLoading(true);
        handleConfirm();
      }
    }
  }

  function handleConfirm() {
    const orderId = Number(methods?.getValues("order.id"));

    createQueue({ schedulingId: orderId, truckId });
  }

  if (isLoading || methods.formState.isLoading) return <LoadingOverlay visible loaderProps={{ size: "xl", type: "bars" }} overlayProps={{ backgroundOpacity: 1 }} />;

  if (queueSaved || !!error.status) {
    navigate({ to: "/app/agendamento/status" });
  }

  return (
    <>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} className={cn({ "my-4 rounded-lg p-6 shadow-md": true, "w-full max-w-lg has-[>.orders]:max-w-5xl": true })}>
        <FormProvider {...methods}>
          <TruckMessageWarning schedulingTotal={schedulingTotal} anonPage={anonPage} truckTotal={truckTotal} />
          <SchedulingQueue schedulingTotal={schedulingTotal} anonPage={anonPage} truckTotal={truckTotal} onConfirmAnon={handleConfirmAnon} />

          {/* Seleção de caminhão */}
          {!ifAnon && schedulingTotal === 0 && (
            <TruckRadioGroup control={methods.control} trucks={trucks} buttonLabel="Próximo" buttonDisabled={methods.formState.isLoading} onButtonClick={handleNext} />
          )}

          {/* Seleção de caminhão para autônomo */}
          {schedulingTotal === 0 && anonPage === 2 && truckTotal > 0 && (
            <TruckRadioGroup
              control={methods.control}
              trucks={trucks}
              buttonLabel="Confirmar"
              buttonDisabled={!methods.watch("truck.plate") || !methods.formState.isValid}
              buttonLoading={methods.formState.isSubmitting}
              buttonType="submit"
            />
          )}

          {/* Pedidos autorizados */}
          {schedulingTotal > 0 && (
            <div className="orders flex flex-col gap-4">
              <QueueHeader Icon={MdPlaylistAddCheck} className="text-primary">
                Pedidos Autorizados
              </QueueHeader>

              <RadioOrder handleAcceptQueue={acceptOrder} handleDenyOrder={denyOrder} />

              <Divider
                variant="dotted"
                color="primary"
                my="md"
                size="sm"
                label={
                  <Anchor component={Link} to="/app/logs/historico">
                    Ver todas as Autorizações Expiradas
                  </Anchor>
                }
              />

              {orderId && (
                <Button type="submit" onClick={handleFrelancerClick} loading={methods.formState.isSubmitting || isLoading} fullWidth>
                  Entrar na fila como Autônomo
                </Button>
              )}
            </div>
          )}

          {/* <pre>{JSON.stringify({ values: methods.getValues(), scheduling, trucks }, null, 2)}</pre> */}
        </FormProvider>
      </form>
    </>
  );
}

interface SchedulingQueueProps {
  onConfirmAnon: () => void;
  schedulingTotal: number;
  anonPage: number;
  truckTotal: number;
}
function SchedulingQueue({ onConfirmAnon, schedulingTotal, anonPage, truckTotal }: SchedulingQueueProps) {
  const { driver } = useSession();
  const companyId = driver?.company.id;
  const { data: settings, isLoading } = useGetSettingsByCompanyQuery({ id: companyId! }, { skip: !companyId });

  const disabled = !settings?.enableQueueWithoutScheduling;

  const handleOnClick = () => {
    if (disabled) return;

    onConfirmAnon();
  };

  const messageMatch = disabled ? "Entre em contato com seu contratante e solicite a vinculação." : "Deseja registrar-se na fila como Autônomo?";

  if (schedulingTotal === 0 && anonPage === 1 && truckTotal !== 0) {
    return (
      <div className="relative flex min-h-32 w-full flex-col gap-3">
        {isLoading && <LoadingOverlay visible />}

        {settings && (
          <>
            <p className="text-center text-gray-700">
              Você não possui pedidos! <br /> {messageMatch}
            </p>

            <div className="flex w-full flex-col items-center">
              <Button component={Link} variant="transparent" to="/app/logs/historico" size="sm" className="hover:underline!">
                Ver todas as Autorizações Expiradas
              </Button>

              <Button size="sm" onClick={handleOnClick} disabled={disabled} fullWidth>
                Entrar na Fila
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

interface TruckMessageWarningProps {
  schedulingTotal: number;
  anonPage: number;
  truckTotal: number;
}
interface TruckRadioGroupProps {
  control: Control<SchedulingSchema>;
  trucks: Truck[];
  buttonLabel: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  buttonType?: "button" | "submit";
  onButtonClick?: () => void;
}

function TruckRadioGroup({ control, trucks, buttonLabel, buttonDisabled, buttonLoading, buttonType = "button", onButtonClick }: TruckRadioGroupProps) {
  return (
    <Controller
      control={control}
      name="truck.plate"
      render={({ field, fieldState: { error }, formState: { isLoading } }) => (
        <div className="flex flex-col gap-4">
          <Radio.Group label="Escolha um caminhão" description="Selecione o caminhão que deseja vincular à fila" error={error?.message} {...field} withAsterisk>
            <Stack pt="md" gap="xs">
              {trucks.map((item) => (
                <Radio.Card key={item.licensePlate} value={String(item.licensePlate)} disabled={isLoading}>
                  <Group wrap="nowrap" align="flex-start">
                    <Radio.Indicator />
                    <div className="flex flex-col gap-2 *:leading-none!">
                      <div className="flex gap-2 *:leading-[inherit]!">
                        <Text size="md" fw="bold" className="text-(--mantine-color-bright)!">
                          {item.licensePlate}
                        </Text>

                        <span className="size-4 rounded-sm border border-gray-500 shadow" style={{ backgroundColor: item.color }}></span>
                      </div>

                      <Text size="xs" className="text-(--mantine-color-dimmed)!">
                        {item.vehicleModel?.name}
                      </Text>
                    </div>
                  </Group>
                </Radio.Card>
              ))}
            </Stack>
          </Radio.Group>

          <Button type={buttonType} onClick={onButtonClick} size="sm" disabled={buttonDisabled || isLoading} loading={buttonLoading} fullWidth>
            {buttonLabel}
          </Button>
        </div>
      )}
    />
  );
}

function TruckMessageWarning({ anonPage, schedulingTotal, truckTotal }: TruckMessageWarningProps) {
  if (schedulingTotal === 0 && anonPage === 1 && truckTotal === 0) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-col place-items-center gap-2">
          <div className="bg-primary-100 text-primary flex size-16 items-center justify-center rounded-full">
            <WarningIcon size={32} weight="bold" />
          </div>

          <h1 className="text-lg font-bold text-gray-700">ATENÇÃO!</h1>
        </div>

        <p className="text-center font-medium text-gray-400">Não há nenhum caminhão no seu cadastro. Edite seu perfil e cadastre um.</p>
      </div>
    );
  }

  return null;
}
